import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { CERTIFICATION_COLORS, CERTIFICATION_ORDER, getEntityEndpoint } from '@/shared/api/openmetadata';
import { getCertificationExpiryDate } from '@/shared/lib/constants';
import { generateCertificationFieldOperations } from '@/shared/lib/patch-operations';
import type { JsonPatchOperation } from '@/shared/types/common';

export type CertificationTag = {
  id: string;
  name: string;
  fullyQualifiedName: string;
  description: string;
  style: {
    color: string;
    iconURL: string;
  };
  version: number;
  updatedAt: number;
  updatedBy: string;
  href: string;
  deprecated: boolean;
  deleted: boolean;
  provider: string;
  disabled: boolean;
  mutuallyExclusive: boolean;
};

export type CertificationOption = {
  id: string;
  name: string;
  fullyQualifiedName: string;
  description: string;
  color: string;
  iconURL?: string;
};

export const fetchAvailableCertifications = async (): Promise<CertificationOption[]> => {
  try {
    const response = await openMetadataApiClient.get<{ data: CertificationTag[] }>('/tags', {
      params: {
        parent: 'Certification',
        limit: 50,
      },
    });

    const certifications = response.data.data || [];

    const certificationOptions = certifications.map((cert) => ({
      id: cert.id,
      name: cert.name,
      fullyQualifiedName: cert.fullyQualifiedName,
      description: cert.description,
      color: CERTIFICATION_COLORS[cert.name] || cert.style?.color || '#8c8c8c',
      iconURL: cert.style?.iconURL,
    }));

    return certificationOptions.sort((a, b) => {
      const certOrder = [...CERTIFICATION_ORDER] as string[];
      const indexA = certOrder.indexOf(a.name);
      const indexB = certOrder.indexOf(b.name);

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  } catch (error) {
    console.error('Failed to fetch available certifications:', error);
    throw error;
  }
};

export const generateCertificationPatchOperation = (
  newCertificationFQN: string | null,
  availableCertifications: CertificationOption[],
  hasCertification: boolean
): JsonPatchOperation[] => {
  const patchOperations: JsonPatchOperation[] = [];

  if (!hasCertification && !newCertificationFQN) {
    return patchOperations;
  }

  if (hasCertification && !newCertificationFQN) {
    patchOperations.push({
      op: 'remove',
      path: '/certification',
    });
    return patchOperations;
  }

  const certInfo = availableCertifications.find((cert) => cert.fullyQualifiedName === newCertificationFQN);
  if (!certInfo) {
    return patchOperations;
  }

  const now = Date.now();
  const expiryDate = getCertificationExpiryDate(now);

  if (!hasCertification && newCertificationFQN) {
    patchOperations.push({
      op: 'add',
      path: '/certification',
      value: {
        tagLabel: {
          tagFQN: certInfo.fullyQualifiedName,
          name: certInfo.name,
          description: certInfo.description,
          source: 'Classification',
          labelType: 'Manual',
          state: 'Confirmed',
          style: {
            color: certInfo.color,
            iconURL: certInfo.iconURL || '',
          },
        },
        appliedDate: now,
        expiryDate: expiryDate,
      },
    });
  } else if (hasCertification && newCertificationFQN) {
    const fieldOperations = generateCertificationFieldOperations({
      appliedDate: now,
      expiryDate: expiryDate,
      tagFQN: certInfo.fullyQualifiedName,
      name: certInfo.name,
      description: certInfo.description,
      style: {
        color: certInfo.color,
        iconURL: certInfo.iconURL || '',
      },
    });

    patchOperations.push(...fieldOperations);
  }

  return patchOperations;
};

export const updateAssetCertification = async (
  entityType: string,
  entityId: string,
  operations: JsonPatchOperation[]
): Promise<void> => {
  const endpoint = getEntityEndpoint(entityType);
  await openMetadataApiClient.patch(`/${endpoint}/${entityId}`, operations, {
    headers: {
      'Content-Type': 'application/json-patch+json',
    },
  });
};
